import { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Alert, apiBaseUrl, Badge, Card, DataTable, Spinner, tokenStore, useFetch } from '@greenrinse/ui';

type VanState = 'Live' | 'NoSignal' | 'OffShift' | 'NeverSeen';
interface LiveVan {
  vanId: string; name: string; plate: string; state: VanState; lat: number | null; lng: number | null; accuracyMeters: number | null;
  speedKmh: number | null; heading: number | null; lastAtUtc: string | null; shiftId: string | null; teamName: string | null;
  siteId: string | null; siteName: string | null; startedBy: string | null; distanceToSiteKm: number | null;
}
interface Trail { distanceKm: number; totalPoints: number; points: { atUtc: string; lat: number; lng: number }[] }
interface RouteStop {
  visitId: string; order: number; siteName: string; partnerName: string | null; lat: number; lng: number; status: string;
  plannedVehicles: number; serviceMinutes: number; distanceKm: number; etaUtc: string; departUtc: string;
}
interface Route { date: string; timezone: string; startFrom: 'Van' | 'FirstStop'; startLat: number | null; startLng: number | null; plannedOrderKm: number; optimisedKm: number; stops: RouteStop[] }

const COLORS: Record<VanState, string> = { Live: '#1E8E3E', NoSignal: '#E8A317', OffShift: '#7A8580', NeverSeen: '#7A8580' };
const TONES: Record<VanState, 'success' | 'warning' | 'default'> = { Live: 'success', NoSignal: 'warning', OffShift: 'default', NeverSeen: 'default' };
const LABELS: Record<VanState, string> = { Live: 'Live', NoSignal: 'No signal', OffShift: 'Off shift', NeverSeen: 'No position yet' };

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

function vanIcon(v: LiveVan, selected: boolean) {
  const c = COLORS[v.state];
  const ring = selected ? `box-shadow:0 0 0 4px ${c}55;` : '';
  return L.divIcon({
    className: '',
    html: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap"><div style="flex:none;width:18px;height:18px;border-radius:50%;background:${c};border:3px solid #fff;${ring}box-shadow:0 0 0 1px ${c}"></div><span style="background:#fff;border-radius:6px;padding:1px 6px;font:600 12px system-ui;color:#17261E;box-shadow:0 1px 3px #0003">${escapeHtml(v.name)}</span></div>`,
    iconSize: [18, 18], iconAnchor: [9, 9],
  });
}
const stopIcon = (n: number) => L.divIcon({
  className: '',
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#1F3A93;color:#fff;font:700 12px/22px system-ui;text-align:center;border:2px solid #fff;box-shadow:0 1px 3px #0005">${n}</div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function ago(iso: string | null) {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
}
const timeIn = (iso: string, timeZone: string) => new Intl.DateTimeFormat(undefined, { timeStyle: 'short', timeZone }).format(new Date(iso));

/** Fit the map once to whatever is worth showing (positioned vans, or the selected van's trail + route). */
function FitBounds({ points, fitKey }: { points: [number, number][]; fitKey: string }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 14);
    else if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);
  return null;
}

export default function LiveMapPage() {
  const vans = useFetch<LiveVan[]>('/api/tracking/vans');
  const [selected, setSelected] = useState<string | null>(null);
  const trail = useFetch<Trail>(selected ? `/api/tracking/vans/${selected}/trail` : null, [selected]);
  const route = useFetch<Route>(selected ? `/api/tracking/vans/${selected}/route` : null, [selected]);
  const [, setTick] = useState(0);

  // Live positions over SignalR; a 60 s refresh catches state changes (no signal, shift ended) and missed pushes.
  useEffect(() => {
    const conn = new HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/hubs/ops`, { accessTokenFactory: () => tokenStore.get()?.accessToken ?? '' })
      .withAutomaticReconnect().configureLogging(LogLevel.Warning).build();
    conn.on('vanMoved', (m: { vanId: string; shiftId: string; lat: number; lng: number; accuracyMeters: number | null; speedKmh: number | null; heading: number | null; lastAtUtc: string }) => {
      vans.setData((list) => list?.map((v) => v.vanId === m.vanId
        ? { ...v, lat: m.lat, lng: m.lng, accuracyMeters: m.accuracyMeters, speedKmh: m.speedKmh, heading: m.heading, lastAtUtc: m.lastAtUtc, state: v.shiftId ? 'Live' : v.state }
        : v) ?? list);
    });
    conn.start().catch(() => { /* the periodic refresh still works */ });
    const refresh = window.setInterval(() => { vans.reload(); setTick((t) => t + 1); }, 60_000);
    return () => { window.clearInterval(refresh); void conn.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const list = vans.data ?? [];
  const positioned = list.filter((v) => v.lat != null && v.lng != null);
  const sel = list.find((v) => v.vanId === selected) ?? null;
  const r = route.data;

  const routeLine = useMemo<[number, number][]>(() => {
    if (!r || r.stops.length === 0) return [];
    const start: [number, number][] = r.startLat != null && r.startLng != null ? [[r.startLat, r.startLng]] : [];
    return [...start, ...r.stops.map((s) => [s.lat, s.lng] as [number, number])];
  }, [r]);
  const trailLine = useMemo<[number, number][]>(() => (trail.data?.points ?? []).map((p) => [p.lat, p.lng]), [trail.data]);

  const fitPoints: [number, number][] = selected
    ? [...trailLine, ...routeLine, ...(sel?.lat != null && sel?.lng != null ? [[sel.lat, sel.lng] as [number, number]] : [])]
    : positioned.map((v) => [v.lat!, v.lng!]);
  const fitKey = selected ? `${selected}:${trail.data ? 1 : 0}:${r ? 1 : 0}` : `all:${positioned.length}`;

  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1 style={{ margin: 0 }}>Live map</h1>
        <span className="gr-muted" style={{ fontSize: 13 }}>Vans share their location only while a shift is open in the operator app.</span>
      </div>
      {vans.error && <Alert tone="error">{vans.error}</Alert>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: 16, alignItems: 'start' }} className="gr-live-grid">
        <Card small style={{ padding: 0, overflow: 'hidden' }}>
          <MapContainer center={[25.2048, 55.2708]} zoom={10} style={{ height: 560, width: '100%' }} scrollWheelZoom>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={fitPoints} fitKey={fitKey} />
            {trailLine.length > 1 && <Polyline positions={trailLine} pathOptions={{ color: '#2C7BE5', weight: 4, opacity: 0.8 }} />}
            {routeLine.length > 1 && <Polyline positions={routeLine} pathOptions={{ color: '#1F3A93', weight: 3, dashArray: '8 8' }} />}
            {r?.stops.map((s) => (
              <Marker key={s.visitId} position={[s.lat, s.lng]} icon={stopIcon(s.order)}>
                <Tooltip>{s.order}. {s.siteName} · ETA {timeIn(s.etaUtc, r.timezone)}</Tooltip>
              </Marker>
            ))}
            {positioned.map((v) => (
              <Marker key={v.vanId} position={[v.lat!, v.lng!]} icon={vanIcon(v, v.vanId === selected)} eventHandlers={{ click: () => setSelected(v.vanId) }}>
                <Tooltip direction="top" offset={[0, -12]}>{v.name} · {LABELS[v.state]} · {ago(v.lastAtUtc)}{v.speedKmh != null ? ` · ${Math.round(v.speedKmh)} km/h` : ''}</Tooltip>
              </Marker>
            ))}
          </MapContainer>
        </Card>

        <Card small style={{ padding: 0, maxHeight: 560, overflowY: 'auto' }}>
          {vans.loading && !vans.data ? <div style={{ padding: 24 }}><Spinner /></div> : list.length === 0 ? <p className="gr-muted" style={{ padding: 16 }}>No active vans.</p> : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {list.map((v) => (
                <li key={v.vanId}>
                  <button type="button" onClick={() => setSelected(v.vanId === selected ? null : v.vanId)}
                    style={{ all: 'unset', boxSizing: 'border-box', display: 'block', width: '100%', cursor: 'pointer', padding: '12px 16px', borderBottom: '1px solid var(--gr-outline-variant, #DCE8DF)', background: v.vanId === selected ? 'var(--gr-primary-container)' : undefined }}>
                    <div className="gr-row" style={{ justifyContent: 'space-between' }}>
                      <strong>{v.name} <span className="gr-muted" style={{ fontWeight: 400 }}>· {v.plate}</span></strong>
                      <Badge tone={TONES[v.state]}>{LABELS[v.state]}</Badge>
                    </div>
                    <div className="gr-muted" style={{ fontSize: 13, marginTop: 4 }}>
                      {v.teamName ? <>{v.teamName} at {v.siteName}{v.distanceToSiteKm != null ? ` · ${v.distanceToSiteKm.toFixed(1)} km away` : ''}<br /></> : null}
                      Last position {ago(v.lastAtUtc)}{v.speedKmh != null && v.state === 'Live' ? ` · ${Math.round(v.speedKmh)} km/h` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {sel && (
        <Card>
          <div className="gr-row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>{sel.name} — today's route</h2>
            <span className="gr-muted" style={{ fontSize: 13 }}>
              {trail.data ? `Driven ${trail.data.distanceKm.toFixed(1)} km in the last 12 h (${trail.data.totalPoints} positions)` : ''}
            </span>
          </div>
          {route.loading && !r ? <Spinner /> : route.error ? <Alert tone="error">{route.error}</Alert> : !r || r.stops.length === 0 ? (
            <p className="gr-muted">No open scheduled visits for this van today.</p>
          ) : (
            <>
              <p className="gr-muted" style={{ marginTop: 8 }}>
                Best order from {r.startFrom === 'Van' ? "the van's current position" : 'the first stop'}: {r.optimisedKm.toFixed(1)} km
                {r.plannedOrderKm - r.optimisedKm >= 0.1 ? ` (planned order would be ${r.plannedOrderKm.toFixed(1)} km)` : ''}. Times are in {r.timezone}; ETAs are estimates from distance, average speed and time on site.
              </p>
              <DataTable<RouteStop>
                rows={r.stops} rowKey={(s) => s.visitId}
                columns={[
                  { key: 'o', header: '#', render: (s) => <strong>{s.order}</strong>, width: 40 },
                  { key: 'site', header: 'Site', render: (s) => <>{s.siteName}{s.partnerName ? <span className="gr-muted"> · {s.partnerName}</span> : null}</> },
                  { key: 'veh', header: 'Vehicles', render: (s) => s.plannedVehicles, align: 'end' },
                  { key: 'km', header: 'Drive', render: (s) => `${s.distanceKm.toFixed(1)} km`, align: 'end' },
                  { key: 'eta', header: 'ETA', render: (s) => <strong>{timeIn(s.etaUtc, r.timezone)}</strong>, align: 'end' },
                  { key: 'dep', header: 'Done by', render: (s) => timeIn(s.departUtc, r.timezone), align: 'end' },
                  { key: 'st', header: '', render: (s) => s.status === 'InProgress' ? <Badge tone="accent">In progress</Badge> : null },
                ]}
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
}
